import React, { useCallback } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import s from './WarehouseHeader.module.css';
import gs from '../../../styles/index.module.css';
import { sendRenameWarehouseRequest } from '../../store/slices/boardSlice';

const Tooltip = ({ totalInfo, warehouseId, tooltipId }) => {
    const tooltipData = totalInfo[warehouseId];
    const pluralizeWords = [
        i18next.t('BoardHeader.return.position'),
        i18next.t('BoardHeader.return.position_genitive'),
        i18next.t('BoardHeader.return.positions')
    ];

    return (
        <ReactTooltip id={tooltipId} className={gs.tooltipTheme} arrowColor="transparent">
            {!!tooltipData && (
                <div className={s.totalInfoWrapper}>
                    <div className={s.totalInfo}>
                        {`${tooltipData.inventory_count} 
                        ${helpable.pluralizeWord(tooltipData.inventory_count, pluralizeWords)} 
                        ${i18next.t('BoardHeader.return.for_the_amount')} 
                        ${numberFormatter.formatMoney(tooltipData.total_inventory_cost, 2, tooltipData.currency.sign)} 
                        `}
                    </div>
                    <div>
                        {`${i18next.t('BoardHeader.return.written_off')} 
                        ${tooltipData.decommissioned_inventory_count} 
                        ${helpable.pluralizeWord(tooltipData.decommissioned_inventory_count, pluralizeWords)}
                        ${i18next.t('BoardHeader.return.for_the_amount')} 
                        ${numberFormatter.formatMoney(tooltipData.decommissioned_inventory_cost, 2, tooltipData.currency.sign)} 
                        `}
                    </div>
                </div>
            )}
        </ReactTooltip>
    );
};

export class WarehouseHeader extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            inputValue: this.props.data.title,
        };
        this.tooltipId = String(Math.floor(Math.random() * 100) + this.props.data.id);
        this.onEditWarehouseButtonClick = this.onEditWarehouseButtonClick.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
        this.saveWarehouseName = this.saveWarehouseName.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    componentWillUnmount() {
        ReactTooltip.rebuild();
    }

    onKeyDown(e) {
        if (e.key === 'Enter') {
            this.props.onWarehouseRename();
            this.saveWarehouseName();
        }
    }

    onInputChange(e) {
        if (e.target.value.trim() === '') {
            this.setState({
                inputValue: this.props.data.title,
            });

            return;
        }
        this.setState({
            inputValue: e.target.value,
        });
    }

    onEditWarehouseButtonClick() {
        this.props.onEditWarehouseButtonClick();
    }

    saveWarehouseName() {
        this.props.renameWarehouse({
            newWarehouseTitle: this.state.inputValue.trim(),
            data: this.props.data,
        });
    }

    render() {
        const {
            isRenameWarehouseMode,
            data,
            onWarehouseRename,
            isEditWarehouseMode,
            hasFullAccess,
            warehouseTotalInfo,
            filterValue,
        } = this.props;

        return (
            <div className={s.warehouseHeaderWrapper}>
                <div className={s.warehouseHeader}>
                    <div
                        className={cx(s.warehouseHeaderTitle, {
                            [s.editWarehouseHeaderTitle]: isRenameWarehouseMode,
                        })}
                    >
                        <span>
                            {data.is_attached_to_project && <div className={s.houseIcon} />}
                        </span>
                        {!isRenameWarehouseMode && (
                            <h2 className={gs.textEllipsis}>{data.title}</h2>
                        )}
                        {isRenameWarehouseMode && (
                            <input
                                maxLength="255"
                                defaultValue={data.title}
                                onChange={this.onInputChange}
                                onKeyDown={this.onKeyDown}
                                autoFocus
                                onBlur={() => {
                                    onWarehouseRename();
                                    this.saveWarehouseName();
                                }}
                                onFocus={(event) => event.target.select()}
                                className={s.warehouseHeaderInput}
                            />
                        )}
                    </div>
                    {!isRenameWarehouseMode && !isEditWarehouseMode && !filterValue && (
                        <>
                            <div
                                className={cx(s.infoWarehouse, s.button)}
                                data-for={this.tooltipId}
                                data-tip
                            />
                            {this.props.data && (
                                <Tooltip
                                    totalInfo={warehouseTotalInfo}
                                    warehouseId={data.id}
                                    tooltipId={this.tooltipId}
                                />
                            )}
                        </>
                    )}
                    {(!isRenameWarehouseMode && !isEditWarehouseMode || !hasFullAccess && data.items.length > 0) && (
                        <div
                            className={cx(s.warehouseHeaderEdit, s.button)}
                            onClick={this.onEditWarehouseButtonClick}
                        />
                    )}
                </div>
            </div>
        );
    }
}

export function WarehouseHeaderContainer(props) {
    const dispatch = useDispatch();
    const renameWarehouse = useCallback(
        (payload) => {
            dispatch(sendRenameWarehouseRequest(payload));
        },
        [dispatch]
    );
    const filterValue = useSelector((state) => state.filter);
    const hasFullAccess = useSelector((state) => state.restrictions);
    const warehouseTotalInfo = useSelector((state) => state.warehouseTotalInfo);

    return (
        <WarehouseHeader
            {...props}
            renameWarehouse={renameWarehouse}
            hasFullAccess={hasFullAccess}
            filterValue={filterValue}
            warehouseTotalInfo={warehouseTotalInfo}
        />
    );
}
